import { useEffect, useRef, useState } from 'react'
import { t } from '../i18n/index.js'

export interface Signature {
  blob: Blob
  takenAt: string
}

/**
 * A finger signature on the phone, e.g. from the tenant after work inside the
 * flat. Drawn black on white on purpose: the server stores photos as JPEG, and
 * a transparent background would turn black there.
 */
export function SignaturePad({
  label,
  signature,
  onChange,
}: {
  label: string
  signature: Signature | null
  onChange: (signature: Signature | null) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const last = useRef<{ x: number; y: number } | null>(null)
  const [empty, setEmpty] = useState(signature === null)
  // The handlers read this, not the state: a quick stroke can end before
  // React has rendered the first line, and the signature would be lost.
  const inked = useRef(signature !== null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    // Sized in device pixels so the line stays sharp on retina screens.
    const ratio = window.devicePixelRatio || 1
    const { width, height } = canvas.getBoundingClientRect()
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(ratio, ratio)
    clearCanvas(ctx, width, height)
  }, [])

  function point(event: React.PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  function start(event: React.PointerEvent<HTMLCanvasElement>) {
    event.currentTarget.setPointerCapture(event.pointerId)
    drawing.current = true
    last.current = point(event)
  }

  function move(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || !last.current) return
    const ctx = event.currentTarget.getContext('2d')
    if (!ctx) return
    const next = point(event)
    ctx.strokeStyle = '#111'
    ctx.lineWidth = 2.4
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(last.current.x, last.current.y)
    ctx.lineTo(next.x, next.y)
    ctx.stroke()
    last.current = next
    if (!inked.current) {
      inked.current = true
      setEmpty(false)
    }
  }

  function end() {
    if (!drawing.current) return
    drawing.current = false
    last.current = null
    const canvas = canvasRef.current
    if (!canvas || !inked.current) return
    canvas.toBlob((blob) => blob && onChange({ blob, takenAt: new Date().toISOString() }), 'image/png')
  }

  function clear() {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const { width, height } = canvas.getBoundingClientRect()
    clearCanvas(ctx, width, height)
    inked.current = false
    setEmpty(true)
    onChange(null)
  }

  return (
    <div className={`wk-signature ${empty ? '' : 'filled'}`}>
      <div className="slot-label">{label}</div>
      <canvas
        ref={canvasRef}
        aria-label={label}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onLostPointerCapture={end}
      />
      <div className="wk-signature-foot">
        <span className="muted">{empty ? t('task.signature_hint') : ' '}</span>
        {!empty && (
          <button type="button" className="retake" onClick={clear}>
            {t('task.signature_clear')}
          </button>
        )}
      </div>
    </div>
  )
}

function clearCanvas(ctx: CanvasRenderingContext2D, width: number, height: number) {
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, width, height)
}
